import * as Sentry from '@sentry/nextjs';
import { v4 as UUID } from 'uuid';

import type { DTOFileInfo } from 'src/apis/dto/DTOFileInfo';
import { client } from 'src/utils/api';

import { resizeAndCompressImage } from './imageUtils';

interface GetContentManagerResponse {
  group: string;
  type: string;
  contentManagerApi: {
    url: string;
    certDigest: null | string;
  };
}

export const getContentManagerAPI = () =>
  client.get<GetContentManagerResponse>('/api/contexts/content-manager');

interface PutContentManagerRequest extends GetContentManagerResponse {
  contentId: string;
  body: Blob;
  contentType: string;
  extension: string;
}

export const putContentManagerAPI = ({
  group,
  type,
  contentManagerApi: { url },
  contentId,
  contentType,
  body,
  extension,
}: PutContentManagerRequest) =>
  client.put(`${url}/groups/${group}/types/${type}/contents/${contentId}.${extension}`, body, {
    headers: { 'Content-Type': contentType },
  });

export const uploadContentManager = async (file: Blob, skipResize = false) => {
  const contentManager = await getContentManagerAPI().then(({ data }) => data);
  const contentId = UUID();
  let resized = file;
  if (!skipResize) {
    resized = await resizeAndCompressImage(file).catch((error) => {
      /**
       * @description
       * content-manager 를 통해서 업로드하는 모든 이미지들은 리사이징이 필요함.
       * 관련 스레드 - https://hpcnt.slack.com/archives/C07HFF9NB98/p1753263150276139
       *
       * 유저가 이미지 파일을 업로드 했지만 13MB가 넘어 MPaaS 리뷰 작업에서 실패하는 이슈가 있었음
       * 엣지 케이스로 파일은 문제 없는데 이미지 리사이징 과정에서 실패 한 경우 로그를 남기고 원본 파일을 반환
       * 로그에서 집계 되면 mime type, size로 특정한 데이터를 찾아 정책을 변경하려고 함
       * 관련 스레드 - https://hpcnt.slack.com/archives/C07HFF9NB98/p1741136403016049
       */
      Sentry.captureException(error, { extra: { file } });

      return file;
    });
  }
  const extension = resized.type.substring(resized.type.indexOf('/') + 1);

  await putContentManagerAPI({
    ...contentManager,
    body: resized,
    contentId,
    contentType: resized.type,
    extension,
  });

  const fileInfo: DTOFileInfo = {
    bucketName: contentManager.group,
    objectName: `${contentManager.type}/${contentId}.${extension}`,
  };

  return fileInfo;
};
